import {Discount, QuotationProductEdit, QuotationProductProps, QuotationReadonlyProductSchema} from "../types";
import {computeTotals} from "../quotation-draft/lib/utilities";
import Checkbox from "../../../ui-components/Checkbox";
import IconButton from "../../../ui-components/IconButton";
import {PencilSquareIcon, EyeIcon} from "@heroicons/react/24/outline";
import React, {useContext, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import CountryContext from "../../../CountryContext";
import {EditProductQuantityModal} from "../edit-product-quantity-modal";
import {round} from "lodash";
import {EditProductDiscountModal} from "../edit-product-discount-modal";
import {ProductImage} from "./product-image";
import {TechnicalSheet} from "./technical-sheet";
import _ from "lodash";
import authenticationService from "@services/AuthenticationService";
import {TintometerKcolorPriceModal} from "../tintometer-kcolor-price-modal";
import getKColorResultContextKey from "../../../utils/getKColorResultContextKey";

export const DEFAULT_EDIT: QuotationProductEdit = {
  checked: true,
  discount: {type: 'no_discount'} as Discount,
  quantity: null,
};

export const QuotationProduct = ({
  posix,
  editable,
  productInfo,
  productEdits,
  setProductEdits,
  originalProductEdits,
  onCheck,
  slim=false,
  setProducts,
}: QuotationProductProps) => {

  // context
  const {country: {code}} = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const userScopes = authenticationService.getUserScopes()

  // state
  const [quantityModelOpen, setQuantityModelOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [tintometerKColorPriceModalOpen, setTintometerKColorPriceModalOpen] = useState(false);

  // data
  const {
    checked,
    discount,
    quantity: updatedQuantity
  } = productEdits ?? DEFAULT_EDIT;
  const {
    product: {
      art_des_it,
      art_des_cmpy,
      base_um: um,
      prezzo: price,
      unita_mis_resa: um_resa,
      min_qty,
      link_image,
    },
    quantity,
  } = productInfo;
  const name = productInfo.isKcolor ? `${productInfo.product.product_des} ${productInfo.kcolor_color_code} ${productInfo.product.min_qty}${productInfo.product.base_um?.toLowerCase()}`
    : code === 'it'
    ? art_des_it ?? art_des_cmpy
    : art_des_cmpy;

  const totals = computeTotals(quantity, min_qty, price, discount, updatedQuantity);

  const userData = authenticationService.getUserData();

  // utilities
  const getDiscountMarginalityMessage = () => {
    if (userScopes.includes('quotation:apply_discount') && userScopes.includes('quotation:apply_marginality')) {
      if (!editable)
        return {id: 'view_discount_marginality', defaultMessage: 'Visualizza sconto/ricarico'};
      if (discount.type === 'no_discount')
        return {id: 'add_discount_marginality', defaultMessage: 'Aggiungi sconto/ricarico'};
      return {id: 'update_discount_marginality', defaultMessage: 'Modifica sconto/ricarico'};
    }
    if (userScopes.includes('quotation:apply_discount')) {
      if (!editable)
        return {id: 'view_discount', defaultMessage: 'Visualizza sconto'};
      if (discount.type === 'no_discount')
        return {id: 'add_discount', defaultMessage: 'Aggiungi sconto/ricarico'};
      return {id: 'update_discount', defaultMessage: 'Modifica sconto/ricarico'};
    }
    if (userScopes.includes('quotation:apply_marginality')) {
      if (!editable)
        return {id: 'view_marginality', defaultMessage: 'Visualizza ricarico'};
      if (discount.type === 'no_discount')
        return {id: 'add_marginality', defaultMessage: 'Aggiungi ricarico'};
      return {id: 'update_marginality', defaultMessage: 'Modifica ricarico'};
    }
  }

  const actions = [
    {
      title: <FormattedMessage id={'edit_quantity'} />,
      onClick: () => { setQuantityModelOpen(true); },
    },
    {
      title: <FormattedMessage {...getDiscountMarginalityMessage()} />,
      onClick: () => {
        setDiscountModalOpen(true); 
      },
    },
    ...(!_.isEqual(productEdits, originalProductEdits)
      ? [{
        title: <span className="text-red-600 font-semibold">
          <FormattedMessage id="reset" />
        </span>,
        onClick: () => {
          if (!window.confirm(intl.formatMessage({id: 'reset_product_edits_confirmation'})))
            return;
          setProductEdits({...originalProductEdits});
        },
      }]
      : []
    ),
  ];

  const containerCls = [
    (slim ? 'p-4' : 'py-6 px-5'),
    'flex gap-4 border',
    (checked || slim) ? (posix % 2 === 0 ? 'bg-white' : 'bg-ivory') : 'bg-gray-100',
    ...(posix > 0 ? ['border-t-0'] : []),
  ].join(' ');
  const gridCls = `grid grid-cols-3 gap-y-1 sm:gap-y-2 gap-x-2 sm:gap-x-10 items-start sm:items-center text-xs ${!slim ? "sm:text-sm" : ""}`;

  return <>
    <div className={containerCls}>
      {/* -------------------- COLUMN 1: checkbox -------------------- */}
      {onCheck && <Checkbox
        id={`product-${posix}`}
        name={`product-${posix}`}
        checked={checked}
        onChange={onCheck}
      />}
      {/* -------------------- COLUMN 2: product info -------------------- */}
      <div className={`flex-1 flex flex-col ${slim ? "gap-3" : "gap-5"} -mt-1 sm:-mt-1.5 ${!editable && 'ml-1'} `}>
        <h2 className={`${checked || slim ? 'font-bold' : 'line-through text-gray-400'} ${slim ? 'text-sm mt-1' : 'text-base sm:text-xl'}`}>
          {name}
        </h2>
        {(checked || slim) && (
          <div className="flex-1 flex items-start justify-between gap-4">
            <div className="h-full flex flex-col justify-between">
              {/* ---------- TECHNICAL SHEET ---------- */}
              <TechnicalSheet
                productInfo={productInfo}
                updatedQuantity={updatedQuantity}
                slim={slim}
                totals={totals}/>
            </div>
          </div>
        )}
        {
          !editable &&
          !!productEdits &&
          productEdits?.discount.type !== 'no_discount' &&
          (productInfo.product.prezzo !== undefined && productInfo.product.prezzo !== null) &&
            <IconButton
                Icon={EyeIcon}
                onClick={() => setDiscountModalOpen(true)}
                styleType="white"
            >
                <FormattedMessage {...getDiscountMarginalityMessage()}/>
            </IconButton>
        }
      </div>
      {/* -------------------- COLUMN 3: product image + edit button -------------------- */}
      {(checked || slim) && (
        <div className={`flex flex-col items-end gap-5 ${slim ? "" : "-mt-1"}`}>
          {editable && (
            productInfo.isKcolor &&
            userData.role_type === 'resale_tintometer' &&
            productInfo.product.prezzo === undefined  || productInfo.product.prezzo === null?
              <IconButton
                Icon={PencilSquareIcon}
                onClick={() => setTintometerKColorPriceModalOpen(true)}
              /> :
            <IconButton
                Icon={PencilSquareIcon}
                subitems={actions}
                subitemsDropdownCls="right-0"
                subitemsDropdownStyleType="white"
                subitemsDropdownCloseOnClick={true}
                subitemsDropdownBig
              />
          )}
          <ProductImage link_image={link_image} slim={slim} />
        </div>
      )}
    </div>

    {editable && quantityModelOpen && (
      <EditProductQuantityModal
        originalQuantity={quantity}
        currentQuantity={updatedQuantity ?? quantity}
        minQty={min_qty}
        um={um}
        onConfirm={(qty) => {
          const reset = !!min_qty && round(Math.ceil(quantity / min_qty) * min_qty) === qty;
          setProductEdits({...productEdits!, quantity: !reset ? qty : null});
        }}
        onReset={
          !_.isEqual(productEdits?.quantity, originalProductEdits?.quantity) ?
            () => {
              setProductEdits({...productEdits!, quantity: originalProductEdits.quantity});
            } : undefined
        }
        onExit={() => { setQuantityModelOpen(false); }}
      />
    )}

    {discountModalOpen && (
      <EditProductDiscountModal
        currentTotal={totals.discountedQuantityOnly}
        discount={discount}
        quantity={updatedQuantity ?? quantity}
        price={price}
        minQty={min_qty}
        onReset={!_.isEqual(productEdits?.discount, originalProductEdits?.discount) ?
          () => {
            setProductEdits({...productEdits!, discount: originalProductEdits.discount});
          } : undefined}
        onConfirm={(d) => { setProductEdits({...productEdits!, discount: d}); }}
        onExit={() => { setDiscountModalOpen(false); }}
        selectedProducts={[productInfo]}
        editable={editable}
      />
    )}

    {tintometerKColorPriceModalOpen && setProducts && (
      <TintometerKcolorPriceModal
        onConfirm={(finalUnitPrice:number) => {
          setProducts((prev) => {
            const newProducts = {...prev};
            const key = getKColorResultContextKey(
              productInfo.product.code,
              productInfo.kcolor_color_code,
              productInfo.product.min_qty
            )
            newProducts[key!!] = {
              ...productInfo,
              product: {
                ...productInfo.product,
                prezzo: finalUnitPrice
              }
            };
            return newProducts;
          });
          setTintometerKColorPriceModalOpen(false);
        }}
        onExit={() => setTintometerKColorPriceModalOpen(false)}
        productInfo={productInfo}
      />
    )}

  </>
}